import React, { useState, useEffect, useMemo } from 'react';
import { GetMarketingStats } from './apis/get';
import { useAuth } from './features/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoaderService from '../services/LoaderService';
import { BarChart3, Calendar, Search, ArrowUpDown, Phone, MessageSquare, AlertCircle, Clock, XCircle, CheckCircle, Eye, X, FileText } from 'lucide-react';

const AVAILABLE_PERIODS = [
  { label: 'All Time', value: 'all' },
  { label: 'Today', value: 'current' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: 'last_7_days' },
  { label: 'Last 15 Days', value: 'last_15_days' },
  { label: 'Last 30 Days', value: 'last_30_days' },
  { label: 'Last 3 Months', value: 'last_3_months' },
];

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'Interested', value: 'Interested' },
  { label: 'Busy Call Later', value: 'Busy Call Later' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Not Interested', value: 'Not Interested' },
  { label: 'Declined', value: 'Declined' },
  { label: 'Escalate to Sonia', value: 'Escalate to Sonia' },
];

const SORT_OPTIONS = [
  { label: 'Updated Date', value: 'updated_at' },
  { label: 'Created Date', value: 'created_at' },
  { label: 'Agent Name', value: 'agent_name' },
  { label: 'Customer Name', value: 'customer_name' },
];

export default function MarketingStats() {
  const { role } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'updated_at', direction: 'desc' });
  const [filters, setFilters] = useState({
    status: '',
    agent_search: '',
    customer_search: '',
    search: '',
    from_date: '',
    to_date: '',
    sort_by: 'updated_at',
    sort_order: 'desc',
  });
  
  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (role && role !== 'SUPER_ADMIN') {
      navigate('/dashboard');
    }
  }, [role, navigate]);

  useEffect(() => {
    if (role === 'SUPER_ADMIN') {
      // Reset pagination when period changes
      setPage(0);
      fetchStats(0, rowsPerPage, filters);
    }
  }, [role, period]);

  // Fetch when page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchStats(newPage, rowsPerPage, filters);
  };

  const [aggregates, setAggregates] = useState({
    total: 0, interested: 0, not_interested: 0, pending: 0, declined: 0, busy: 0 
  });

  const isSuccessResponse = (response: any) => {
    return response?.success === true || response?.status === true || response?.status === 'success';
  };



  const fetchStats = async (pageIndex: number, limit: number, currentFilters = filters) => {
    setLoading(true);
    LoaderService.show();
    try {
      const offset = pageIndex * limit;
      const response = await GetMarketingStats(period, limit, offset, currentFilters);
      
      if (isSuccessResponse(response)) {
         const data = Array.isArray(response.data) ? response.data : [];
         setStats(data);
         const total = response.count ?? response.totalCount ?? response.total ?? data.length;
         setTotalCount(total); 

         if (response.totals) {
            setAggregates({
               total: response.count ?? total,
               interested: response.totals.interested || 0,
               not_interested: response.totals.not_interested || 0,
               pending: response.totals.pending || 0,
               busy: response.totals.busy_call_later || 0,
               declined: response.totals.declined || 0
            });
         } else {
             // Fallback to client-side calc if simple list
             const newAggs = data.reduce((acc: any, curr: any) => ({
                total: acc.total + 1,
                interested: acc.interested + (curr.interested === 1 || curr.status === 'Interested' ? 1 : 0),
                not_interested: acc.not_interested + (curr.not_interested === 1 || curr.status === 'Not Interested' ? 1 : 0),
                pending: acc.pending + (curr.pending === 1 || curr.status === 'Pending' ? 1 : 0),
                busy: acc.busy + (curr.busy_call_later === 1 || curr.status === 'Busy Call Later' ? 1 : 0),
                declined: acc.declined + (curr.declined === 1 || curr.status === 'Declined' ? 1 : 0),
              }), { total: 0, interested: 0, not_interested: 0, pending: 0, declined: 0, busy: 0 });
              setAggregates(newAggs);
         }
      } else {
        console.error("Failed to fetch stats", response);
        setStats([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching marketing stats:', error);
      setStats([]);
    } finally {
      setLoading(false);
      LoaderService.hide();
    }
  };



  const filteredAndSortedStats = useMemo(() => stats, [stats]);

  const handleSort = (key: string) => {
    const direction: 'asc' | 'desc' = sortConfig?.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    const nextSort = {
      key,
      direction
    };
    const sortKeyMap: Record<string, string> = {
      assigned_person_name: 'agent_name',
      user_name: 'customer_name',
      updated_at: 'updated_at',
      created_at: 'created_at',
    };
    const nextFilters = {
      ...filters,
      sort_by: sortKeyMap[key] || key,
      sort_order: nextSort.direction,
    };
    setSortConfig(nextSort);
    setFilters(nextFilters);
    setPage(0);
    fetchStats(0, rowsPerPage, nextFilters);
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilterChange = (name: string, value: string) => {
    const nextFilters = { ...filters, [name]: value };
    setFilters(nextFilters);
    if (name === 'sort_by' || name === 'sort_order') {
      const sortDisplayMap: Record<string, string> = {
        agent_name: 'assigned_person_name',
        customer_name: 'user_name',
      };
      setSortConfig({
        key: sortDisplayMap[nextFilters.sort_by] || nextFilters.sort_by,
        direction: nextFilters.sort_order as 'asc' | 'desc',
      });
    }
    setPage(0);
    fetchStats(0, rowsPerPage, nextFilters);
  };

  const applyFilters = () => {
    const sortDisplayMap: Record<string, string> = {
      agent_name: 'assigned_person_name',
      customer_name: 'user_name',
    };
    setPage(0);
    setSortConfig({ key: sortDisplayMap[filters.sort_by] || filters.sort_by, direction: filters.sort_order as 'asc' | 'desc' });
    fetchStats(0, rowsPerPage, filters);
  };

  const clearFilters = () => {
    const nextFilters = {
      status: '',
      agent_search: '',
      customer_search: '',
      search: '',
      from_date: '',
      to_date: '',
      sort_by: 'updated_at',
      sort_order: 'desc',
    };
    setFilters(nextFilters);
    setSortConfig({ key: 'updated_at', direction: 'desc' });
    setPage(0);
    fetchStats(0, rowsPerPage, nextFilters);
  };

  if (role !== 'SUPER_ADMIN') return null;

  return (
    <div className="min-h-screen bg-transparent text-white font-sans p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <h1 className="text-3xl font-bold text-yellow-300 tracking-wide flex items-center gap-3">
              <BarChart3 size={32} />
              Marketing Activity Log
            </h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 w-full lg:max-w-5xl">
              <div className="flex items-center gap-3 bg-slate-900/50 p-1 rounded-lg border border-slate-700">
                <Calendar className="text-gray-400 ml-2" size={16} />
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="bg-transparent text-white text-sm font-medium focus:outline-none p-2 cursor-pointer [&>option]:bg-slate-900 min-w-0 w-full"
                >
                  {AVAILABLE_PERIODS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              <select
                value={filters.status}
                onChange={(e) => applyFilterChange('status', e.target.value)}
                className="bg-slate-900/50 border border-slate-700 text-sm rounded-lg focus:ring-yellow-500 focus:border-yellow-500 p-2 text-white [&>option]:bg-slate-900"
              >
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value || 'all'} value={option.value}>{option.label}</option>
                ))}
              </select>

              <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Agent name, number, ID"
                  className="bg-slate-900/50 border border-slate-700 text-sm rounded-lg focus:ring-yellow-500 focus:border-yellow-500 block w-full pl-10 p-2 text-white placeholder-gray-500 transition-all group-hover:bg-slate-900"
                  value={filters.agent_search}
                  onChange={(e) => handleFilterChange('agent_search', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                />
              </div>

              <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Customer name, number, ID"
                  className="bg-slate-900/50 border border-slate-700 text-sm rounded-lg focus:ring-yellow-500 focus:border-yellow-500 block w-full pl-10 p-2 text-white placeholder-gray-500 transition-all group-hover:bg-slate-900"
                  value={filters.customer_search}
                  onChange={(e) => handleFilterChange('customer_search', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                />
              </div>

              <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search all fields"
                  className="bg-slate-900/50 border border-slate-700 text-sm rounded-lg focus:ring-yellow-500 focus:border-yellow-500 block w-full pl-10 p-2 text-white placeholder-gray-500 transition-all group-hover:bg-slate-900"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                />
              </div>

              <input
                type="date"
                value={filters.from_date}
                onChange={(e) => applyFilterChange('from_date', e.target.value)}
                className="bg-slate-900/50 border border-slate-700 text-sm rounded-lg focus:ring-yellow-500 focus:border-yellow-500 p-2 text-white"
              />

              <input
                type="date"
                value={filters.to_date}
                onChange={(e) => applyFilterChange('to_date', e.target.value)}
                className="bg-slate-900/50 border border-slate-700 text-sm rounded-lg focus:ring-yellow-500 focus:border-yellow-500 p-2 text-white"
              />

              <div className="flex gap-2">
                <select
                  value={filters.sort_by}
                  onChange={(e) => applyFilterChange('sort_by', e.target.value)}
                  className="bg-slate-900/50 border border-slate-700 text-sm rounded-lg focus:ring-yellow-500 focus:border-yellow-500 p-2 text-white [&>option]:bg-slate-900 min-w-0 flex-1"
                >
                  {SORT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select
                  value={filters.sort_order}
                  onChange={(e) => applyFilterChange('sort_order', e.target.value)}
                  className="bg-slate-900/50 border border-slate-700 text-sm rounded-lg focus:ring-yellow-500 focus:border-yellow-500 p-2 text-white [&>option]:bg-slate-900"
                >
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </select>
              </div>

              <div className="flex gap-2 xl:col-span-4 justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-slate-900/60 border border-slate-700 text-slate-300 rounded-lg text-sm font-bold hover:bg-slate-800 transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={applyFilters}
                  className="px-5 py-2 bg-yellow-300 text-black rounded-lg text-sm font-black hover:bg-yellow-400 transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
        </div>

        {/* Aggregate Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
          {[
             { label: 'Interested', value: aggregates.interested, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' },
             { label: 'Busy / Later', value: aggregates.busy, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
             { label: 'Pending', value: aggregates.pending, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
             { label: 'Not Interested', value: aggregates.not_interested, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
          ].map((card, i) => (
             <div key={i} className={`${card.bg} backdrop-blur-sm border rounded-xl p-3 flex flex-col items-center justify-center transform transition-all hover:scale-105`}>
                <span className={`text-2xl font-black ${card.color}`}>{card.value}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-1 text-center">{card.label}</span>
             </div>
          ))}
        </div>

        {/* Main Table */}
        <div className="bg-slate-900/40 backdrop-blur-xl border border-white/10 shadow-2xl rounded-2xl overflow-hidden min-h-[500px] flex flex-col">
           <div className="overflow-x-auto flex-grow">
             <table className="w-full text-left border-collapse">
               <thead>
                 <tr className="bg-slate-950/80 text-gray-400 text-xs uppercase tracking-wider">
                    <SortableHeader label="Agent" sortKey="assigned_person_name" currentSort={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Customer" sortKey="user_name" currentSort={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Instruction" sortKey="instruction" currentSort={sortConfig} onSort={handleSort} />
                    <th className="p-4 font-bold">Feedback</th>
                    <SortableHeader label="Updated" sortKey="updated_at" currentSort={sortConfig} onSort={handleSort} />
                 </tr>
               </thead>
               <tbody className="divide-y divide-white/5">
                 {loading ? (
                    <tr>
                      <td colSpan={6} className="p-12 text-center">
                         <div className="flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-300"></div></div>
                      </td>
                    </tr>
                 ) : filteredAndSortedStats.length === 0 ? (
                    <tr><td colSpan={6} className="p-12 text-center text-gray-500 text-sm">No records found matching your criteria.</td></tr>
                 ) : (
                    filteredAndSortedStats.map((row, idx) => (
                      <DetailedRow key={row.customer_id || `${row.user_mobile || 'row'}-${idx}`} row={row} />
                    ))
                 )}
               </tbody>
             </table>
           </div>

           {/* Pagination Footer */}
           <Pagination 
              page={page + 1} 
              setPage={(p: number) => handlePageChange(p - 1)} 
              totalPages={Math.ceil(totalCount / rowsPerPage)} 
              totalCount={totalCount} 
           />
        </div>
      </div>
    </div>
  );
}

const SortableHeader = ({ label, sortKey, currentSort, onSort }: any) => (
   <th 
     className="p-4 font-bold cursor-pointer hover:text-white transition-colors group select-none whitespace-nowrap"
     onClick={() => onSort(sortKey)}
   >
     <div className="flex items-center gap-1">
       {label}
       <ArrowUpDown size={12} className={`transition-opacity ${currentSort?.key === sortKey ? 'opacity-100 text-yellow-300' : 'opacity-30 group-hover:opacity-60'}`} />
     </div>
   </th>
);

const DetailedRow = ({ row }: { row: any }) => {
   const [showInstruction, setShowInstruction] = useState(false);

   const getStatusBadge = (row: any) => {
      let statusText = row.status || 'Pending';
      let colorClass = 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      let Icon = Clock;

      if (row.interested === 1 || statusText === 'Interested') {
         statusText = 'Interested';
         colorClass = 'bg-green-500/20 text-green-300 border-green-500/30';
         Icon = CheckCircle;
      } else if (row.not_interested === 1 || statusText === 'Not Interested') {
         statusText = 'Not Interested';
         colorClass = 'bg-red-500/20 text-red-300 border-red-500/30';
         Icon = XCircle;
      } else if (row.busy_call_later === 1 || statusText === 'Busy Call Later') {
         statusText = 'Busy / Later';
         colorClass = 'bg-amber-500/20 text-amber-300 border-amber-500/30';
         Icon = Clock;
      } else if (row.declined === 1 || statusText === 'Declined') {
         statusText = 'Declined';
         colorClass = 'bg-gray-600/30 text-gray-400 border-gray-600/50';
         Icon = XCircle;
      } else if (row.pending === 1) {
        statusText = 'Pending';
        colorClass = 'bg-blue-500/20 text-blue-300 border-blue-500/30';
        Icon = AlertCircle;
      }

      return (
         <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
            <Icon size={12} />
            {statusText}
         </span>
      );
   };

   return (
      <>
         <tr className="hover:bg-white/5 transition-colors group">
            <td className="p-4">
               <div className="flex flex-col">
                  <span className="text-white font-medium text-sm">{row.assigned_person_name || row.assigned_to}</span>
                  <div className="flex items-center gap-1 text-xs text-indigo-300 mt-0.5">
                     <Phone size={10} />
                     {row.assigned_person_mobile || 'N/A'}
                  </div>
                  {row.assigned_person_id && <span className="text-[10px] text-slate-500 mt-0.5">ID: {row.assigned_person_id}</span>}
               </div>
            </td>
            <td className="p-4">
               <div className="flex flex-col">
                  <span className="text-white font-medium text-sm">{row.user_name || 'Unknown User'}</span>
                  <div className="flex items-center gap-1 text-xs text-emerald-300 mt-0.5">
                      <Phone size={10} />
                      {row.user_mobile || 'N/A'}
                  </div>
                  {row.customer_id && <span className="text-[10px] text-slate-500 mt-0.5">ID: {row.customer_id}</span>}
               </div>
            </td>
            <td className="p-4">
               {getStatusBadge(row)}
               {row.priority && <div className="mt-1 text-[10px] uppercase font-bold text-slate-500 tracking-wider">Prior: {row.priority}</div>}
            </td>
            <td className="p-4 text-sm text-gray-300">
               {row.instruction ? (
                  <button 
                    onClick={() => setShowInstruction(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 rounded-lg text-amber-300 transition-all text-xs font-bold group/btn"
                  >
                    <FileText size={14} className="group-hover/btn:scale-110 transition-transform" />
                    View Instruction
                  </button>
               ) : <span className="text-gray-600 text-xs italic">None</span>}
            </td>
            <td className="p-4">
                {row.feedback ? (
                    <div className="flex items-start gap-2 max-w-xs">
                       <MessageSquare size={14} className="mt-1 text-blue-400 flex-shrink-0" />
                       <span className="text-sm text-gray-200">{row.feedback}</span>
                    </div>
                ) : (
                   <span className="text-gray-600 text-xs italic">No feedback</span>
                )}
            </td>
            <td className="p-4">
               <div className="flex flex-col text-xs text-gray-400">
                  <span className="font-semibold text-gray-300">{new Date(row.updated_at || row.created_at).toLocaleDateString()}</span>
                  <span>{new Date(row.updated_at || row.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
               </div>
            </td>
         </tr>

         {/* Instruction Modal */}
         {showInstruction && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
               <div 
                 className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
                 onClick={() => setShowInstruction(false)}
               />
               <div className="relative bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                  <div className="flex items-center justify-between p-4 border-b border-white/5">
                     <h3 className="text-lg font-bold text-yellow-300 flex items-center gap-2">
                        <FileText size={20} />
                        Marketing Instruction
                     </h3>
                     <button 
                       onClick={() => setShowInstruction(false)}
                       className="p-1 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
                     >
                        <X size={20} />
                     </button>
                  </div>
                  <div className="p-6">
                     <div className="p-4 bg-slate-950/50 rounded-xl border border-white/5 text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">
                        {row.instruction}
                     </div>
                     
                     <div className="mt-6 flex flex-col gap-3">
                        <div className="flex items-center justify-between text-xs transition-opacity">
                           <span className="text-gray-500 uppercase font-black tracking-widest">Target Customer</span>
                           <span className="text-emerald-400 font-bold">{row.user_name || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                           <span className="text-gray-500 uppercase font-black tracking-widest">Assigned Agent</span>
                           <span className="text-indigo-400 font-bold">{row.assigned_person_name || row.assigned_to}</span>
                        </div>
                     </div>
                  </div>
                  <div className="p-4 bg-slate-950/30 flex justify-end">
                     <button 
                       onClick={() => setShowInstruction(false)}
                       className="px-6 py-2 bg-yellow-300 text-black font-bold rounded-lg hover:bg-yellow-400 transition-colors shadow-lg"
                     >
                        Got it
                     </button>
                  </div>
               </div>
            </div>
         )}
      </>
   );
};




const Pagination = ({ page, setPage, totalPages, totalCount }: any) => {
  const getPageNumbers = () => {
    const pages = [];
    const showEllipsisStart = page > 4;
    const showEllipsisEnd = page < totalPages - 3;
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (showEllipsisStart) pages.push("...");
      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);
      if (!showEllipsisStart) { start = 2; end = 5; }
      if (!showEllipsisEnd) { end = totalPages - 1; start = totalPages - 4; }
      for (let i = start; i <= end; i++) pages.push(i);
      if (showEllipsisEnd) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 border-t border-white/10 bg-slate-950/50">
      <div className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">
        Total Records: {totalCount}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="h-8 px-3 bg-slate-900 border border-slate-800 text-slate-400 rounded hover:text-white transition-all disabled:opacity-20 flex items-center justify-center"
        >
          &lt;
        </button>
        {getPageNumbers().map((p, idx) => (
          <button
            key={idx}
            onClick={() => typeof p === 'number' && setPage(p)}
            disabled={p === "..."}
            className={`h-8 w-8 text-[11px] font-bold rounded transiton-all ${page === p
              ? "bg-white text-black"
              : p === "..."
                ? "bg-transparent text-slate-600"
                : "bg-slate-900 border border-slate-800 text-slate-400 hover:text-white"
              }`}
          >
            {p}
          </button>
        ))}
        <button
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="h-8 px-3 bg-slate-900 border border-slate-800 text-slate-400 rounded hover:text-white transition-all disabled:opacity-20 flex items-center justify-center"
        >
          &gt;
        </button>
      </div>
    </div>
  );
};
